import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { OrganizationDto, OrganizationService } from '../services/organization.service';
import { HelcimAddressRequest, HelcimInvoiceLineItemContract, HelcimInvoiceResponse, TestPaymentService } from '../services/test-payment.service';
import { PortalNavbarComponent } from './portal-navbar.component';

interface QuickLink {
  label: string;
  route: string;
  icon: string;
}

@Component({
  selector: 'app-payment-services-invoices-get-invoice-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <div class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/payment-services" class="home-btn">Payment Services</a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--emerald">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                <line x1="2" y1="10" x2="22" y2="10"></line>
              </svg>
            </div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">Payment Services > Invoices > Get Invoice</h1>
          <p class="page-sub">View and edit invoice details for the selected organization.</p>
        </div>
      </div>

      <section class="page-body">
        <div class="page-body-inner">
          <div class="related-card related-card--top">
            <h3>Quick Access</h3>
            <div class="related-grid">
              @for (link of relatedLinks; track link.route) {
                <a class="quick-card" [routerLink]="link.route">
                  <div class="quick-card-hd">
                    <span class="quick-icon">{{ link.icon }}</span>
                    <span>{{ link.label }}</span>
                  </div>
                </a>
              }
            </div>
          </div>

          <div class="card">
            <div class="card-hd">
              <span class="card-title">Invoice Details</span>
            </div>

            <div class="form">
              <label class="field">
                <span>Organization</span>
                <select [(ngModel)]="organizationId" name="organizationId" [disabled]="isLoadingOrganizations || isLoading" required>
                  <option [ngValue]="0">{{ isLoadingOrganizations ? 'Loading organizations...' : 'Select organization' }}</option>
                  @for (organization of organizations; track organization.id) {
                    <option [ngValue]="organization.id">{{ organization.displayName }} ({{ organization.legalName }})</option>
                  }
                </select>
              </label>

              <label class="field">
                <span>Invoice Id</span>
                <input type="text" [(ngModel)]="invoiceId" name="invoiceId" maxlength="120" />
              </label>

              <div class="actions actions--query">
                <button type="button" class="btn btn--primary" [disabled]="isLoading || organizationId <= 0 || !invoiceId.trim()" (click)="loadInvoice()">
                  {{ isLoading ? 'Loading...' : 'Get Invoice' }}
                </button>
              </div>

              <label class="field"><span>Customer Id</span><input type="text" [(ngModel)]="form.customerId" name="customerId" maxlength="64" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Invoice Number</span><input type="text" [(ngModel)]="form.invoiceNumber" name="invoiceNumber" maxlength="64" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Currency</span><input type="text" [(ngModel)]="form.currency" name="currency" maxlength="3" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Date Issued</span><input type="text" [(ngModel)]="form.dateIssued" name="dateIssued" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Type</span><input type="text" [(ngModel)]="form.type" name="type" maxlength="32" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Status</span><input type="text" [(ngModel)]="form.status" name="status" maxlength="32" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Convenience Fee Enabled</span><input type="checkbox" [(ngModel)]="form.convenienceFeeEnabled" name="convenienceFeeEnabled" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Tip Amount</span><input type="number" [(ngModel)]="form.tipAmount" name="tipAmount" step="0.01" (ngModelChange)="onModelChange()" /></label>
              <label class="field field--full"><span>Notes</span><input type="text" [(ngModel)]="form.notes" name="notes" maxlength="250" (ngModelChange)="onModelChange()" /></label>

              <div class="section-title">Billing Address</div>
              <label class="field"><span>Name</span><input type="text" [(ngModel)]="form.billingAddress.name" name="billingName" maxlength="120" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Street 1</span><input type="text" [(ngModel)]="form.billingAddress.street1" name="billingStreet1" maxlength="120" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Street 2</span><input type="text" [(ngModel)]="form.billingAddress.street2" name="billingStreet2" maxlength="120" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>City</span><input type="text" [(ngModel)]="form.billingAddress.city" name="billingCity" maxlength="80" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Province</span><input type="text" [(ngModel)]="form.billingAddress.province" name="billingProvince" maxlength="40" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Country</span><input type="text" [(ngModel)]="form.billingAddress.country" name="billingCountry" maxlength="40" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Postal Code</span><input type="text" [(ngModel)]="form.billingAddress.postalCode" name="billingPostalCode" maxlength="20" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Phone</span><input type="text" [(ngModel)]="form.billingAddress.phone" name="billingPhone" maxlength="32" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Email</span><input type="email" [(ngModel)]="form.billingAddress.email" name="billingEmail" maxlength="120" (ngModelChange)="onModelChange()" /></label>

              <div class="section-title">Shipping Address</div>
              <label class="field"><span>Name</span><input type="text" [(ngModel)]="form.shippingAddress.name" name="shippingName" maxlength="120" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Street 1</span><input type="text" [(ngModel)]="form.shippingAddress.street1" name="shippingStreet1" maxlength="120" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Street 2</span><input type="text" [(ngModel)]="form.shippingAddress.street2" name="shippingStreet2" maxlength="120" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>City</span><input type="text" [(ngModel)]="form.shippingAddress.city" name="shippingCity" maxlength="80" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Province</span><input type="text" [(ngModel)]="form.shippingAddress.province" name="shippingProvince" maxlength="40" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Country</span><input type="text" [(ngModel)]="form.shippingAddress.country" name="shippingCountry" maxlength="40" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Postal Code</span><input type="text" [(ngModel)]="form.shippingAddress.postalCode" name="shippingPostalCode" maxlength="20" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Phone</span><input type="text" [(ngModel)]="form.shippingAddress.phone" name="shippingPhone" maxlength="32" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Email</span><input type="email" [(ngModel)]="form.shippingAddress.email" name="shippingEmail" maxlength="120" (ngModelChange)="onModelChange()" /></label>

              <div class="section-title">Line Items (JSON)</div>
              <label class="field field--full"><span>Line Items</span><textarea rows="7" [(ngModel)]="lineItemsJson" name="lineItemsJson" (ngModelChange)="onLineItemsChange()"></textarea></label>

              <div class="actions actions--save">
                <button type="button" class="btn btn--primary" [disabled]="isSaving || !isDirty || organizationId <= 0 || !invoiceId.trim()" (click)="saveInvoice()">
                  {{ isSaving ? 'Saving...' : 'Save Invoice' }}
                </button>
              </div>
            </div>

            @if (errorMessage) {
              <div class="result result--error">{{ errorMessage }}</div>
            }

            @if (successMessage) {
              <div class="result result--ok">{{ successMessage }}</div>
            }
          </div>

        </div>
      </section>
    </div>
  `,
  styles: [`.page{min-height:100vh}.page-hero{border-bottom:1px solid var(--color-border);padding:2rem 1.5rem;background:linear-gradient(155deg,var(--color-bg-start) 0%,var(--color-bg-end) 100%)}.page-hero-inner,.page-body-inner{max-width:1080px;margin:0 auto}.home-btn{color:var(--color-muted);text-decoration:none}.hero-top-row{align-items:center;display:flex;flex-wrap:wrap;gap:.85rem;margin-top:.75rem}.hero-icon{align-items:center;border-radius:0.9rem;display:flex;height:56px;justify-content:center;width:56px}.hero-icon--emerald{background:color-mix(in srgb,#10b981 18%,transparent);border:1px solid color-mix(in srgb,#10b981 35%,transparent);color:#34d399}.page-title{margin:.8rem 0 0}.page-sub{margin:.4rem 0 0;color:var(--color-muted)}.page-body{padding:1.5rem}.card,.related-card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent)}.card{padding-bottom:1rem}.related-card{padding:1rem 1.1rem}.related-card--top{margin-bottom:1rem}.related-card h3{margin:0 0 .55rem}.related-grid{display:grid;gap:.8rem;grid-template-columns:repeat(auto-fit,minmax(220px,1fr))}.quick-card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent);padding:.85rem 1rem;text-decoration:none;color:var(--color-text);transition:transform .15s ease,border-color .15s ease}.quick-card:hover{transform:translateY(-2px);border-color:color-mix(in srgb,var(--color-accent) 38%,var(--color-border))}.quick-card-hd{display:flex;align-items:center;gap:.5rem}.quick-icon{display:inline-flex;align-items:center;justify-content:center;width:1.2rem;height:1.2rem}.card-hd{border-bottom:1px solid var(--color-border);padding:.8rem 1rem}.card-title{font-weight:600}.form{display:grid;grid-template-columns:1fr 1fr;gap:.8rem;padding:1rem}.section-title{grid-column:1 / -1;font-weight:600;margin-top:.35rem}.field{display:flex;flex-direction:column;gap:.35rem}.field--full{grid-column:1 / -1}.field input,.field select,.field textarea{border:1px solid var(--color-border);border-radius:.45rem;background:transparent;color:var(--color-text);padding:.45rem .55rem}.field select{color-scheme:dark}.field select option{background:var(--color-bg-end);color:var(--color-text)}.actions{grid-column:1 / -1;display:flex;justify-content:flex-start}.actions--query{padding-top:.25rem}.actions--save{padding-top:.25rem}.btn{align-items:center;border:1px solid transparent;border-radius:.6rem;cursor:pointer;display:inline-flex;font-size:.78rem;font-weight:600;gap:.35rem;line-height:1;padding:.6rem .9rem;transition:all .18s ease}.btn:disabled{cursor:not-allowed;opacity:.55}.btn--primary{background:linear-gradient(135deg,#2563eb,#1d4ed8);border-color:#1d4ed8;color:#fff}.result{margin:0 1rem .9rem;padding:.75rem;border-radius:.55rem;font-size:.9rem;display:grid;gap:.25rem}.result--ok{border:1px solid color-mix(in srgb,#10b981 40%,transparent);background:color-mix(in srgb,#10b981 10%,transparent)}.result--error{border:1px solid color-mix(in srgb,#ef4444 40%,transparent);background:color-mix(in srgb,#ef4444 10%,transparent)}`]
})
export class PaymentServicesInvoicesGetInvoicePageComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly organizationService = inject(OrganizationService);
  private readonly testPaymentService = inject(TestPaymentService);
  private readonly route = inject(ActivatedRoute);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  organizationId = 0;
  invoiceId = '';
  organizations: OrganizationDto[] = [];
  selectedOrganization: OrganizationDto | null = null;
  isLoadingOrganizations = false;

  isLoading = false;
  isSaving = false;
  isDirty = false;
  errorMessage = '';
  successMessage = '';

  private originalModel: string | null = null;

  form = {
    customerId: '',
    invoiceNumber: '',
    currency: 'USD',
    dateIssued: '',
    type: '',
    status: '',
    convenienceFeeEnabled: false,
    tipAmount: 0,
    notes: '',
    billingAddress: createAddress(),
    shippingAddress: createAddress(),
    lineItems: [] as HelcimInvoiceLineItemContract[]
  };

  lineItemsJson = '[]';

  readonly relatedLinks: QuickLink[] = [
    { label: 'Get Invoices', route: '/payment-services/invoices/get-invoices', icon: '🧾' }
  ];

  async ngOnInit(): Promise<void> {
    await this.loadOrganizationsAsync();

    const queryOrganizationId = Number(this.route.snapshot.queryParamMap.get('organizationId') ?? '0');
    const queryInvoiceId = this.route.snapshot.queryParamMap.get('invoiceId') ?? '';

    if (queryOrganizationId > 0) {
      this.organizationId = queryOrganizationId;
    }

    if (queryInvoiceId.trim()) {
      this.invoiceId = queryInvoiceId.trim();
    }

    if (this.organizationId > 0 && this.invoiceId) {
      await this.loadInvoice();
    }
  }

  onLineItemsChange(): void {
    this.successMessage = '';

    try {
      const parsed = JSON.parse(this.lineItemsJson) as HelcimInvoiceLineItemContract[];
      this.form.lineItems = Array.isArray(parsed) ? parsed : [];
      this.onModelChange();
    } catch {
      this.errorMessage = 'Line Items must be valid JSON array.';
    }
  }

  onModelChange(): void {
    this.successMessage = '';
    this.errorMessage = this.errorMessage === 'Line Items must be valid JSON array.' ? '' : this.errorMessage;
    this.isDirty = this.originalModel !== null && this.originalModel !== JSON.stringify(this.form);
  }

  private async loadOrganizationsAsync(): Promise<void> {
    this.isLoadingOrganizations = true;

    try {
      const isSiteAdmin = await this.authService.hasRoleAsync('SiteAdmin');
      this.organizations = isSiteAdmin
        ? await firstValueFrom(this.organizationService.getAll())
        : [await firstValueFrom(this.organizationService.getMyOrganization())];

      if (this.organizationId <= 0 && this.organizations.length === 1) {
        this.organizationId = this.organizations[0].id;
      }

      this.syncSelectedOrganization();
    } catch {
      this.organizations = [];
      this.selectedOrganization = null;
      this.errorMessage = 'Unable to load organizations.';
    } finally {
      this.isLoadingOrganizations = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  async loadInvoice(): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';

    if (this.organizationId <= 0 || !this.invoiceId.trim()) {
      this.errorMessage = 'Organization and invoice id are required.';
      return;
    }

    this.isLoading = true;

    try {
      const response = await this.testPaymentService.getInvoice(this.organizationId, this.invoiceId.trim()).toPromise();

      if (!response) {
        throw new Error('Get invoice request returned no data.');
      }

      this.applyResponse(response);
      this.applyOrganizationBusinessNamesToAddresses();
      this.originalModel = JSON.stringify(this.form);
      this.isDirty = false;
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Get invoice request failed.';
    } finally {
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  async saveInvoice(): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';

    if (this.organizationId <= 0 || !this.invoiceId.trim()) {
      this.errorMessage = 'Organization and invoice id are required.';
      return;
    }

    if (!this.isDirty) {
      return;
    }

    this.isSaving = true;

    try {
      this.applyOrganizationBusinessNamesToAddresses();

      const response = await this.testPaymentService.updateInvoice(this.invoiceId.trim(), {
        organizationId: this.organizationId,
        invoiceId: this.invoiceId.trim(),
        customerId: this.form.customerId.trim() || undefined,
        invoiceNumber: this.form.invoiceNumber.trim() || undefined,
        currency: this.form.currency.trim() || undefined,
        dateIssued: this.form.dateIssued.trim() || undefined,
        lineItems: this.form.lineItems,
        billingAddress: toRequestAddress(this.form.billingAddress),
        shippingAddress: toRequestAddress(this.form.shippingAddress),
        type: this.form.type.trim() || undefined,
        status: this.form.status.trim() || undefined,
        convenienceFeeEnabled: this.form.convenienceFeeEnabled,
        tipAmount: Number.isFinite(this.form.tipAmount) ? this.form.tipAmount : undefined,
        notes: this.form.notes.trim() || undefined
      }).toPromise();

      if (!response) {
        throw new Error('Update invoice request returned no data.');
      }

      this.applyResponse(response);
      this.originalModel = JSON.stringify(this.form);
      this.isDirty = false;
      this.successMessage = 'Invoice saved successfully.';
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Update invoice request failed.';
    } finally {
      this.isSaving = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  private applyResponse(response: HelcimInvoiceResponse): void {
    this.invoiceId = response.invoiceId || this.invoiceId;
    this.form.customerId = response.customerId ?? '';
    this.form.invoiceNumber = response.invoiceNumber ?? '';
    this.form.currency = response.currency ?? 'USD';
    this.form.dateIssued = response.dateIssued ?? '';
    this.form.type = response.type ?? '';
    this.form.status = response.status ?? '';
    this.form.convenienceFeeEnabled = response.convenienceFeeEnabled ?? false;
    this.form.tipAmount = response.tipAmount ?? 0;
    this.form.notes = response.notes ?? '';
    this.form.billingAddress = fromResponseAddress(response.billingAddress);
    this.form.shippingAddress = fromResponseAddress(response.shippingAddress);
    this.form.lineItems = response.lineItems ?? [];
    this.lineItemsJson = JSON.stringify(this.form.lineItems, null, 2);
  }

  private syncSelectedOrganization(): void {
    this.selectedOrganization = this.organizations.find((organization) => organization.id === this.organizationId) ?? null;
  }

  private applyOrganizationBusinessNamesToAddresses(): void {
    this.syncSelectedOrganization();

    const billingBusinessName = this.selectedOrganization?.billingBusinessName?.trim();
    if (billingBusinessName) {
      this.form.billingAddress.name = billingBusinessName;
    }

    const shippingBusinessName = this.selectedOrganization?.shippingBusinessName?.trim();
    if (shippingBusinessName) {
      this.form.shippingAddress.name = shippingBusinessName;
    }
  }
}

function createAddress() {
  return {
    name: '',
    street1: '',
    street2: '',
    city: '',
    province: '',
    country: '',
    postalCode: '',
    phone: '',
    email: ''
  };
}

function fromResponseAddress(address: {
  name?: string;
  street1?: string;
  street2?: string;
  city?: string;
  province?: string;
  country?: string;
  postalCode?: string;
  phone?: string;
  email?: string;
} | undefined) {
  return {
    name: address?.name ?? '',
    street1: address?.street1 ?? '',
    street2: address?.street2 ?? '',
    city: address?.city ?? '',
    province: address?.province ?? '',
    country: address?.country ?? '',
    postalCode: address?.postalCode ?? '',
    phone: address?.phone ?? '',
    email: address?.email ?? ''
  };
}

function toRequestAddress(address: {
  name: string;
  street1: string;
  street2: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  phone: string;
  email: string;
}): HelcimAddressRequest {
  return {
    name: address.name.trim(),
    street1: address.street1.trim(),
    street2: address.street2.trim() || undefined,
    city: address.city.trim(),
    province: address.province.trim() || undefined,
    country: address.country.trim() || undefined,
    postalCode: address.postalCode.trim(),
    phone: address.phone.trim() || undefined,
    email: address.email.trim() || undefined
  };
}
